import React, { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "lib/supabaseClient";
import { insertLog } from "services/logService";
import {
  MdPostAdd, MdEdit, MdDelete, MdVisibility, MdVisibilityOff,
  MdCloudUpload, MdClose, MdCheck, MdOpenInNew, MdFormatBold,
  MdFormatItalic, MdFormatListBulleted, MdFormatQuote,
  MdImage, MdArticle, MdSchedule,
} from "react-icons/md";

// ─── Rich Text Toolbar ─────────────────────────────────────
const ToolbarBtn = ({ onClick, icon, title, active }) => (
  <button type="button" onClick={onClick} title={title}
    className={`rounded-lg p-2 transition-all ${active ? "bg-[#0055FF] text-white" : "text-gray-500 hover:bg-gray-200 hover:text-white"}`}>
    {React.cloneElement(icon, { size: 16 })}
  </button>
);

const RichEditor = ({ value, onChange }) => {
  const ref = useRef(null);

  const exec = (cmd, val = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(ref.current?.innerHTML || "");
  };

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || "";
    }
  }, []); // only on mount

  return (
    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white overflow-hidden focus-within:border-[#0055FF]/50 transition-all">
      {/* Toolbar */}
      <div className="flex items-center gap-1 border-b border-gray-200 bg-white/[0.02] px-3 py-2 flex-wrap">
        <ToolbarBtn onClick={() => exec("bold")}          icon={<MdFormatBold />}         title="Bold" />
        <ToolbarBtn onClick={() => exec("")}        icon={<MdFormatItalic />}       title="Italic" />
        <ToolbarBtn onClick={() => exec("insertUnorderedList")} icon={<MdFormatListBulleted />} title="List" />
        <ToolbarBtn onClick={() => exec("formatBlock", "blockquote")} icon={<MdFormatQuote />} title="Quote" />
        <div className="mx-2 h-4 w-[1px] bg-gray-200" />
        <ToolbarBtn onClick={() => exec("formatBlock", "h2")}   icon={<span className="text-xs ">H2</span>} title="Heading 2" />
        <ToolbarBtn onClick={() => exec("formatBlock", "h3")}   icon={<span className="text-xs ">H3</span>} title="Heading 3" />
        <ToolbarBtn onClick={() => exec("formatBlock", "p")}    icon={<span className="text-xs">¶</span>} title="Paragraph" />
        <div className="mx-2 h-4 w-[1px] bg-gray-200" />
        <ToolbarBtn onClick={() => exec("removeFormat")} icon={<span className="text-xs">Tx</span>} title="Clear Format" />
      </div>
      {/* Editable area */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current?.innerHTML || "")}
        className="min-h-[200px] p-6 text-sm text-gray-700 leading-relaxed outline-none prose max-w-none"
        style={{ fontFamily: "inherit" }}
        data-placeholder="Start writing article content..."
      />
      <style>{`
        [contenteditable]:empty:before { content: attr(data-placeholder); color: rgba(255,255,255,0.1); }
        [contenteditable] h2 { font-size: 1.3em; font-weight: 600; margin: 12px 0 6px; color: #fff; }
        [contenteditable] h3 { font-size: 1.1em; font-weight: 500; margin: 10px 0 4px; color: #ddd; }
        [contenteditable] blockquote { border-left: 3px solid #0055FF; padding-left: 12px; color: rgba(255,255,255,0.5); margin: 8px 0; font-style: ; }
        [contenteditable] ul { padding-left: 20px; list-style: disc; }
        [contenteditable] li { margin: 4px 0; }
        [contenteditable] strong { color: #fff; }
      `}</style>
    </div>
  );
};

// ─── Preview Modal ──────────────────────────────────────────
const PreviewModal = ({ article, onClose }) => (
  <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/95 backdrop-blur-xl p-4">
    <div className="max-h-[95vh] w-full max-w-3xl overflow-y-auto rounded-[32px] border border-gray-200 bg-white shadow-2xl">
      {/* Cover Image */}
      {article.image_url && (
        <div className="h-64 w-full overflow-hidden rounded-t-[32px]">
          <img src={article.image_url} alt="cover" className="h-full w-full object-cover" />
        </div>
      )}
      <div className="p-10">
        {/* Meta */}
        <div className="mb-4 flex items-center gap-3">
          <span className="rounded-lg bg-[#0055FF]/10 px-3 py-1 text-[9px]  uppercase tracking-widest text-[#0055FF]">
            {article.category}
          </span>
          <span className="text-[9px] text-gray-400 uppercase tracking-widest">
            {new Date(article.created_at || Date.now()).toLocaleDateString("en-GB")}
          </span>
          <span className={`ml-auto rounded-lg px-3 py-1 text-[9px]  uppercase ${article.is_published ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>
            {article.is_published ? "Published" : "Draft"}
          </span>
        </div>
        {/* Title */}
        <h1 className="mb-4 text-3xl font-light uppercase tracking-tight text-core-charcoal leading-tight">
          {article.title || "Untitled Article"}
        </h1>
        {/* Meta desc */}
        {article.meta_description && (
          <p className="mb-8 text-sm text-gray-500  border-l-2 border-[#0055FF] pl-4">
            {article.meta_description}
          </p>
        )}
        {/* Content */}
        <div
          className="prose max-w-none text-sm text-gray-600 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: article.content || "<p>No content yet.</p>" }}
        />
      </div>
      <div className="sticky bottom-0 flex justify-end border-t border-gray-200 bg-white px-10 py-4">
        <button onClick={onClose}
          className="flex items-center gap-2 rounded-2xl bg-gray-100 px-8 py-3 text-xs uppercase text-gray-500 hover:bg-gray-200 transition-all">
          <MdClose size={16} /> Close Preview
        </button>
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════
export default function ArticlesCMS() {
  const [articles,    setArticles]    = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [showEditor,  setShowEditor]  = useState(false);
  const [editingId,   setEditingId]   = useState(null);
  const [preview,     setPreview]     = useState(null);
  const [uploading,   setUploading]   = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [filter,      setFilter]      = useState("All");

  const blank = {
    title: "", content: "", category: "Legal",
    image_url: "", meta_description: "", is_published: true,
  };
  const [form, setForm] = useState(blank);

  // ── Auth + fetch ──
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: p } = await supabase.from("employees").select("*").eq("id", user.id).single();
        setCurrentUser(p);
      }
      fetchArticles();
    })();
  }, []);

  const fetchArticles = async () => {
    const { data } = await supabase.from("articles").select("*").order("created_at", { ascending: false });
    setArticles(data || []);
  };

  // ── Log helper ──
  const log = async (action, details) => {
    if (!currentUser) return;
    try {
      await insertLog({
        timestamp:     new Date().toISOString(),
        actor_name:    currentUser.full_name,
        actor_number:  currentUser.employee_number,
        actor_role:    currentUser.role,
        target_id:     null,
        target_name:   form.title || "Article",
        target_number: null,
        action,
        details,
      });
    } catch {}
  };

  // ── Upload image ──
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const ext  = file.name.split(".").pop();
      const name = `${Date.now()}_core.${ext}`;
      const { error } = await supabase.storage.from("articles").upload(name, file);
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from("articles").getPublicUrl(name);
      setForm(f => ({ ...f, image_url: publicUrl }));
    } catch (err) { alert("Upload failed: " + err.message); }
    finally { setUploading(false); }
  };

  // ── Save ──
  const handleSave = async () => {
    if (!form.title || !form.content) return alert("Title and content are required.");
    setSaving(true);
    try {
      if (editingId) {
        await supabase.from("articles").update(form).eq("id", editingId);
        await log("ARTICLE_UPDATED", `Updated article: "${form.title}" | Category: ${form.category} | Status: ${form.is_published ? "Published" : "Draft"}`);
      } else {
        await supabase.from("articles").insert([form]);
        await log("ARTICLE_CREATED", `Created article: "${form.title}" | Category: ${form.category} | Status: ${form.is_published ? "Published" : "Draft"}`);
      }
      setForm(blank);
      setEditingId(null);
      setShowEditor(false);
      fetchArticles();
    } catch (err) { alert("Save failed: " + err.message); }
    finally { setSaving(false); }
  };

  // ── Toggle visibility ──
  const togglePublish = async (art) => {
    await supabase.from("articles").update({ is_published: !art.is_published }).eq("id", art.id);
    await log(
      art.is_published ? "ARTICLE_UNPUBLISHED" : "ARTICLE_PUBLISHED",
      `${art.is_published ? "Unpublished" : "Published"}: "${art.title}"`
    );
    fetchArticles();
  };

  // ── Delete ──
  const handleDelete = async (art) => {
    if (!window.confirm(`Delete "${art.title}"? This cannot be undone.`)) return;
    await supabase.from("articles").delete().eq("id", art.id);
    await log("ARTICLE_DELETED", `Deleted article: "${art.title}" | Category: ${art.category}`);
    fetchArticles();
  };

  // ── Edit ──
  const handleEdit = async (art) => {
    const { data } = await supabase.from("articles").select("*").eq("id", art.id).single();
    if (data) {
      setForm(data);
      setEditingId(art.id);
      setShowEditor(true);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    }
  };

  const categories = ["All", "Legal", "Financial", "Management"];
  const filtered = filter === "All" ? articles : articles.filter(a => a.category === filter);

  return (
    <div className="min-h-screen bg-white p-4 md:p-8 font-sans">

      {/* ── HEADER ── */}
      <div className="mb-10 flex flex-col md:flex-row items-center justify-between gap-4 border-b border-gray-200 pb-8">
        <div>
          <h1 className="text-3xl font-extralight uppercase  tracking-tighter text-core-charcoal">
            CORE <span className="opacity-20 font-thin">Content</span>
          </h1>
          <p className="mt-2 text-[10px] uppercase tracking-[0.4em] text-gray-500">Articles Management System</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Category filter */}
          <div className="flex gap-1 rounded-2xl border border-gray-200 bg-white/[0.02] p-1">
            {categories.map(c => (
              <button key={c} onClick={() => setFilter(c)}
                className={`rounded-xl px-4 py-2 text-[10px] uppercase tracking-wider transition-all ${filter === c ? "bg-[#0055FF] text-white" : "text-gray-500 hover:text-white"}`}>
                {c}
              </button>
            ))}
          </div>
          <button onClick={() => { setForm(blank); setEditingId(null); setShowEditor(true); }}
            className="flex items-center gap-2 rounded-2xl bg-[#0055FF] px-8 py-4 text-[11px] uppercase text-white shadow-2xl hover:scale-105 transition-all">
            <MdPostAdd size={20} /> New Article
          </button>
        </div>
      </div>

      {/* ── EDITOR (slide in) ── */}
      {showEditor && (
        <div className="mb-10 rounded-[40px] border border-brand-500/20 bg-gray-50 p-8 md:p-12 shadow-2xl">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-light uppercase  text-core-charcoal">
                {editingId ? "Edit Article" : "New Article"}
              </h2>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400">
                {editingId ? "Modify and update" : "Create and publish"}
              </p>
            </div>
            <button onClick={() => { setShowEditor(false); setForm(blank); setEditingId(null); }}
              className="rounded-xl border border-gray-200 bg-gray-100 p-3 text-gray-500 hover:text-white transition-all">
              <MdClose size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {/* Title */}
            <div className="md:col-span-2 flex flex-col gap-2">
              <label className="text-[10px]  uppercase tracking-widest text-gray-500">Article Title *</label>
              <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                placeholder="Enter article headline..."
                className="rounded-2xl border border-gray-200 bg-gray-50 p-5 text-sm text-core-charcoal outline-none focus:border-brand-500/50 transition-all placeholder:text-gray-300" />
            </div>

            {/* Category */}
            <div className="flex flex-col gap-2">
              <label className="text-[10px]  uppercase tracking-widest text-gray-500">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                className="rounded-2xl border border-gray-200 bg-white p-5 text-sm text-white outline-none focus:border-[#0055FF]/50 transition-all">
                <option value="Legal">Legal</option>
                <option value="Financial">Financial</option>
                <option value="Management">Management</option>
              </select>
            </div>

            {/* Meta description */}
            <div className="md:col-span-2 flex flex-col gap-2">
              <label className="text-[10px]  uppercase tracking-widest text-gray-500">Meta Description</label>
              <input value={form.meta_description} onChange={e => setForm({...form, meta_description: e.target.value})}
                placeholder="Short SEO description..."
                className="rounded-2xl border border-gray-200 bg-gray-50 p-5 text-sm text-core-charcoal outline-none focus:border-brand-500/50 transition-all placeholder:text-gray-300" />
            </div>

            {/* Publish toggle */}
            <div className="flex flex-col gap-2 justify-center">
              <label className="text-[10px]  uppercase tracking-widest text-gray-500">Status</label>
              <button onClick={() => setForm({...form, is_published: !form.is_published})}
                className={`flex items-center gap-3 rounded-2xl border px-5 py-4 text-sm  transition-all ${form.is_published ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-400" : "border-red-500/20 bg-red-500/10 text-red-400"}`}>
                <div className={`relative h-5 w-9 rounded-full transition-all ${form.is_published ? "bg-emerald-500" : "bg-red-500/50"}`}>
                  <div className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${form.is_published ? "left-4" : "left-0.5"}`} />
                </div>
                {form.is_published ? "Published" : "Draft"}
              </button>
            </div>

            {/* Cover image */}
            <div className="md:col-span-3 flex flex-col gap-2">
              <label className="text-[10px]  uppercase tracking-widest text-gray-500">Cover Image</label>
              <div className="flex gap-3">
                <input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})}
                  placeholder="Paste image URL or upload..."
                  className="flex-1 rounded-2xl border border-gray-200 bg-white p-5 text-sm font-mono text-white/60 outline-none focus:border-[#0055FF]/50 transition-all placeholder:text-gray-300" />
                <label className={`flex cursor-pointer items-center gap-2 rounded-2xl border border-gray-200 bg-gray-100 px-6 py-4 text-sm text-gray-500 hover:bg-gray-200 transition-all ${uploading ? "opacity-50" : ""}`}>
                  <MdCloudUpload size={20} className={uploading ? "animate-bounce text-[#0055FF]" : ""} />
                  {uploading ? "Uploading..." : "Upload"}
                  <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
                </label>
                {form.image_url && (
                  <div className="h-14 w-20 overflow-hidden rounded-2xl border border-gray-200">
                    <img src={form.image_url} alt="preview" className="h-full w-full object-cover" />
                  </div>
                )}
              </div>
            </div>

            {/* Rich text editor */}
            <div className="md:col-span-3 flex flex-col gap-2">
              <label className="text-[10px]  uppercase tracking-widest text-gray-500">Content *</label>
              <RichEditor value={form.content} onChange={v => setForm({...form, content: v})} />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex gap-4">
            <button onClick={() => setPreview({ ...form, created_at: new Date().toISOString() })}
              className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-gray-100 px-8 py-4 text-xs uppercase text-gray-500 hover:bg-gray-200 transition-all">
              <MdVisibility size={18} /> Preview
            </button>
            <button onClick={() => { setShowEditor(false); setForm(blank); setEditingId(null); }}
              className="flex-1 rounded-2xl bg-gray-100 py-4 text-xs uppercase text-gray-500 hover:bg-gray-200 transition-all">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex-[2] flex items-center justify-center gap-2 rounded-2xl bg-[#0055FF] py-4 text-xs uppercase text-white shadow-2xl hover:brightness-110 active:scale-95 transition-all disabled:opacity-50">
              <MdCheck size={18} />
              {saving ? "Saving..." : editingId ? "Update Article" : "Publish Article"}
            </button>
          </div>
        </div>
      )}

      {/* ── ARTICLES GRID ── */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filtered.map(art => (
          <div key={art.id}
            className={`group relative rounded-[32px] border bg-white/[0.01] overflow-hidden transition-all hover:border-gray-200 hover:shadow-2xl ${art.is_published ? "border-gray-200" : "border-red-500/10 opacity-60"}`}>

            {/* Cover */}
            <div className="relative h-44 bg-gray-50 overflow-hidden">
              {art.image_url ? (
                <img src={art.image_url} alt={art.title} className="h-full w-full object-cover transition-all group-hover:scale-105" />
              ) : (
                <div className="flex h-full items-center justify-center">
                  <MdImage size={40} className="text-gray-200" />
                </div>
              )}
              {/* Status badge */}
              <div className={`absolute top-3 right-3 rounded-lg px-2.5 py-1 text-[8px]  uppercase tracking-widest ${art.is_published ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/20" : "bg-red-500/20 text-red-400 border border-red-500/20"}`}>
                {art.is_published ? "Live" : "Draft"}
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-3 flex items-center gap-2">
                <span className="rounded-lg bg-[#0055FF]/10 px-2.5 py-1 text-[8px]  uppercase tracking-widest text-[#0055FF]">
                  {art.category}
                </span>
                <span className="flex items-center gap-1 text-[8px] text-gray-400 uppercase">
                  <MdSchedule size={10} />
                  {new Date(art.created_at).toLocaleDateString("en-GB")}
                </span>
              </div>
              <h3 className="mb-2 line-clamp-2 text-sm  uppercase tracking-wide text-white leading-snug group-hover:text-[#0055FF] transition-colors">
                {art.title}
              </h3>
              {art.meta_description && (
                <p className="line-clamp-2 text-[11px] text-gray-500  leading-relaxed">
                  {art.meta_description}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 border-t border-gray-200 px-4 py-3">
              <button onClick={() => setPreview(art)} title="Preview"
                className="flex-1 flex items-center justify-center gap-1 rounded-xl py-2 text-[10px] uppercase text-gray-500 hover:bg-gray-100 hover:text-white transition-all">
                <MdVisibility size={14} /> Preview
              </button>
              <button onClick={() => togglePublish(art)} title={art.is_published ? "Unpublish" : "Publish"}
                className={`flex-1 flex items-center justify-center gap-1 rounded-xl py-2 text-[10px] uppercase transition-all ${art.is_published ? "text-emerald-400/60 hover:bg-emerald-500/10 hover:text-emerald-400" : "text-red-400/60 hover:bg-red-500/10 hover:text-red-400"}`}>
                {art.is_published ? <MdVisibility size={14} /> : <MdVisibilityOff size={14} />}
                {art.is_published ? "Published" : "Draft"}
              </button>
              <button onClick={() => handleEdit(art)} title="Edit"
                className="rounded-xl p-2 text-gray-500 hover:bg-[#0055FF]/10 hover:text-[#0055FF] transition-all">
                <MdEdit size={16} />
              </button>
              <button onClick={() => handleDelete(art)} title="Delete"
                className="rounded-xl p-2 text-gray-500 hover:bg-red-500/10 hover:text-red-500 transition-all">
                <MdDelete size={16} />
              </button>
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="col-span-3 py-20 text-center text-gray-300 text-sm uppercase tracking-widest">
            No articles found
          </div>
        )}
      </div>

      {/* ── PREVIEW MODAL ── */}
      {preview && <PreviewModal article={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
